import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import type { AddressValidationOutcome } from "@/common/hooks/use-address-validation";
import { useAddressValidation } from "@/common/hooks/use-address-validation";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { ValidatedAddress } from "@/pages/Replacement/replacement.types";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import { toAddressFormValidationErrors } from "@/services/address-validation/address-validation-errors.mapper";
import type {
  AddressFields,
  ValidationAddress,
  ValidationError,
} from "@/services/address-validation/address-validation.types";
import {
  addressFieldsFor,
  toFormAddress,
  toValidatedAddress,
  withSuggestedAddress,
} from "../address.mapper";
import {
  AddressPrefix,
  ChooseDeliveryFormState,
  EMPTY_ADDRESS_FORM_VALUES,
} from "../choose-delivery-form.types";
import { CHOOSE_DELIVERY_STRINGS } from "../choose-delivery.constants";

const PREFIX: AddressPrefix = "shippingAddress";

type SuggestionOutcome = Extract<AddressValidationOutcome, { status: "suggestionAvailable" }>;

/** What the suggestion modal shows: HOP's suggestion and the address HOP was sent. */
type ShippingAddressSuggestion = Pick<SuggestionOutcome, "mode" | "suggestion"> & {
  enteredAddress: ValidationAddress;
};

interface PendingSuggestion extends ShippingAddressSuggestion {
  validated: ValidatedAddress;
}

const toModalSuggestion = (pending?: PendingSuggestion): ShippingAddressSuggestion | undefined =>
  pending && {
    mode: pending.mode,
    suggestion: pending.suggestion,
    enteredAddress: pending.enteredAddress,
  };

const formValuesFor = (saved?: ValidatedAddress): ChooseDeliveryFormState => ({
  [PREFIX]: saved ? toFormAddress(saved) : EMPTY_ADDRESS_FORM_VALUES,
});

/**
 * HOP's rejection as field errors plus the panel message: the unattributed messages, or a generic one when
 * HOP named no field at all.
 */
const toRejection = (validationErrors: ValidationError[]) => {
  const { fieldErrors, unattributedMessages } = toAddressFormValidationErrors(
    validationErrors,
    addressFieldsFor(PREFIX)
  );
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const genericMessage = hasFieldErrors
    ? undefined
    : getString("addressValidationReasons.addressNotValidated");
  const panelMessage =
    unattributedMessages.length > 0 ? [...new Set(unattributedMessages)].join(" ") : genericMessage;
  return { fieldErrors, panelMessage };
};

/**
 * Owns the shipping address form and its save-and-validate flow. Only `saveAddress` and `confirmSuggestion`
 * write an address to the draft, and they write the very `ValidatedAddress` HOP was sent. Validation fails
 * closed: a failed call never saves. Whether a new address form is open lives in the draft, so Continue sees it.
 */
export const useShippingAddress = () => {
  const {
    shippingAddress,
    isEditing: isEditingInDraft,
    setShippingAddress,
    startEditing,
    stopEditing,
  } = useReplacementDraftStore();
  const { validateAddress } = useAddressValidation();
  /** Errors first show on `Save address`; after that, each field rechecks as the employee fixes it. */
  const form = useForm<ChooseDeliveryFormState>({
    defaultValues: formValuesFor(isEditingInDraft ? undefined : shippingAddress),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const isEditing = !shippingAddress || Boolean(isEditingInDraft);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pendingSuggestion, setPendingSuggestion] = useState<PendingSuggestion | undefined>(
    undefined
  );

  const save = (validated: ValidatedAddress) => {
    setShippingAddress(validated);
    stopEditing();
    form.reset(formValuesFor(validated));
    setError(undefined);
  };

  const showRejection = (validationErrors: ValidationError[]) => {
    const { fieldErrors, panelMessage } = toRejection(validationErrors);
    Object.entries(fieldErrors).forEach(([field, message]) =>
      form.setError(field as keyof typeof fieldErrors, { message })
    );
    setError(panelMessage);
  };

  const applyOutcome = (outcome: AddressValidationOutcome, validated: ValidatedAddress) => {
    switch (outcome.status) {
      case "proceed":
        save(validated);
        return;
      case "suggestionAvailable":
        setPendingSuggestion({
          mode: outcome.mode,
          suggestion: outcome.suggestion,
          enteredAddress: validated.address,
          validated,
        });
        return;
      case "invalid":
        showRejection(outcome.validationErrors);
        return;
      case "crossCountryNotAllowed":
        setError(getString("addressValidationReasons.crossCountryNotAllowed"));
    }
  };

  const validateAndSave = form.handleSubmit(async (values) => {
    const validated = toValidatedAddress(values[PREFIX]);
    try {
      applyOutcome(await validateAddress({ address: validated.address }), validated);
    } catch {
      setError(getString(`${CHOOSE_DELIVERY_STRINGS}.errors.validationFailed`));
    }
  });

  const saveAddress = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    setError(undefined);
    try {
      await validateAndSave();
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  /** Saves the address the employee picked in the modal: HOP's suggestion, or the address HOP was sent. */
  const confirmSuggestion = (address: AddressFields) => {
    if (!pendingSuggestion) return;
    const { validated, enteredAddress } = pendingSuggestion;
    setPendingSuggestion(undefined);
    save(address === enteredAddress ? validated : withSuggestedAddress(validated, address));
  };

  const editNewAddress = () => {
    form.reset({ [PREFIX]: EMPTY_ADDRESS_FORM_VALUES });
    startEditing();
    setError(undefined);
  };

  const cancelEditing = () => {
    form.reset(formValuesFor(shippingAddress));
    stopEditing();
    setError(undefined);
  };

  return {
    form,
    prefix: PREFIX,
    savedAddress: shippingAddress,
    isEditing,
    isSaving,
    error,
    suggestion: toModalSuggestion(pendingSuggestion),
    saveAddress,
    confirmSuggestion,
    closeSuggestion: () => setPendingSuggestion(undefined),
    editNewAddress,
    cancelEditing,
  };
};
